import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SEARCH_MIN_QUERY_LENGTH, type GlobalSearchResultsDto } from '@gty/common';

import { CACHE } from '../../app/query-config';
import { searchService } from '../../services';
import { flattenSearchResults, hasAnyResult, type SearchHit } from './search-model';
import { useDebouncedValue } from './useDebouncedValue';

/**
 * All of the global search's behaviour, with no markup attached.
 *
 * `GlobalSearch.tsx` renders what this returns and owns no logic of its own —
 * which is what makes the keyboard rules below testable by calling a hook
 * rather than by simulating a DOM.
 */

/** Long enough that a touch-typist fires one request per word, not per letter. */
export const SEARCH_DEBOUNCE_MS = 300;

export interface GlobalSearchState {
  term: string;
  setTerm: (next: string) => void;

  open: boolean;
  close: () => void;
  openIfTyped: () => void;

  /** The term the current results belong to. Lags `term` by the debounce. */
  debouncedTerm: string;
  /** True once the debounced term is long enough to have been sent. */
  isQueryable: boolean;

  results: GlobalSearchResultsDto | undefined;
  hits: readonly SearchHit[];
  isLoading: boolean;
  isError: boolean;

  /** -1 when nothing is highlighted. Always addresses a real hit or -1. */
  activeIndex: number;
  setActiveIndex: (next: number) => void;

  /** Show "type at least 2 characters" instead of results. */
  showMinLengthHint: boolean;
  /** Show "no results for X". */
  showNoResults: boolean;

  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  goTo: (hit: SearchHit) => void;
}

export function useGlobalSearch(): GlobalSearchState {
  const navigate = useNavigate();

  const [term, setTermState] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndexState, setActiveIndexState] = useState(-1);

  /**
   * TRIMMED BEFORE DEBOUNCING, so "guru" and "guru " are one cache key and one
   * request rather than two. The server trims as well; doing it here too is not
   * duplication of a RULE, it is normalising the key so the cache can hit.
   */
  const debouncedTerm = useDebouncedValue(term.trim(), SEARCH_DEBOUNCE_MS);
  const isQueryable = debouncedTerm.length >= SEARCH_MIN_QUERY_LENGTH;

  /**
   * THE DEBOUNCED TERM IS IN THE QUERY KEY. That is the whole concurrency story.
   *
   * react-query gives each term its own cache entry and hands `queryFn` an
   * `AbortSignal` that it fires when this component stops caring about that
   * entry, so superseded requests are cancelled in flight AND, if one resolves
   * anyway, it resolves into a key nothing is reading. A stale slow response
   * cannot overwrite a fresh fast one because the two never share a slot.
   *
   * `enabled` is what enforces the minimum length: below it there is no query,
   * so there is no request — not a request whose result we discard.
   *
   * NO `placeholderData: keepPreviousData` here, deliberately. Holding the
   * previous term's rows on screen while a new term loads would let the
   * "No results for X" message render the NEW term above the OLD results, which
   * is worse than a brief loading row: it states something false.
   */
  const query = useQuery({
    queryKey: ['search', 'global', debouncedTerm],
    queryFn: ({ signal }) => searchService.global(debouncedTerm, signal),
    enabled: isQueryable,
    ...CACHE.search,
  });

  /**
   * Results are read ONLY when the debounced term is queryable. Without this
   * guard, clearing the box back to one character would leave the last
   * response's rows on screen — `enabled: false` disables fetching, not the
   * cached `data` that is already there.
   */
  const results = isQueryable ? query.data : undefined;

  const hits = useMemo(() => flattenSearchResults(results), [results]);

  /**
   * RESET THE HIGHLIGHT WHENEVER THE RESULT SET CHANGES.
   *
   * The dangerous case is a SHORTER new list: highlight row 6 of eight results
   * for "guru", type another letter, get three results, press Enter — and the
   * old index addresses nothing. `hits` is memoised on the response object, so
   * this fires exactly when new rows arrive and not on unrelated re-renders.
   */
  useEffect(() => {
    setActiveIndexState(-1);
  }, [hits]);

  /**
   * CLAMPED, NOT MERELY RESET.
   *
   * The effect above runs AFTER the render that first shows the new list, so
   * for one paint the stored index can still exceed the new length. Deriving
   * the index on every render closes that window: `aria-activedescendant`, the
   * highlighted row and Enter all read this, and it can only ever be -1 or a
   * real position. The effect is the tidy-up; this is the guarantee.
   */
  const activeIndex = activeIndexState >= 0 && activeIndexState < hits.length ? activeIndexState : -1;

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndexState(-1);
  }, []);

  const setTerm = useCallback((next: string) => {
    setTermState(next);
    // Typing always re-opens: the panel having been dismissed with Escape must
    // not make the next keystroke feel dead.
    setOpen(true);
  }, []);

  const openIfTyped = useCallback(() => {
    setOpen(true);
  }, []);

  const goTo = useCallback(
    (hit: SearchHit) => {
      close();
      navigate(hit.to);
    },
    [close, navigate],
  );

  /**
   * `hits` and `activeIndex` change on nearly every keystroke, and rebuilding
   * this handler each time would be fine — but the ref keeps `onKeyDown` stable
   * so the input never re-binds mid-interaction.
   */
  const latest = useRef({ hits, activeIndex, open, goTo, close });
  latest.current = { hits, activeIndex, open, goTo, close };

  const onKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    const state = latest.current;

    if (event.key === 'Escape') {
      /**
       * SCOPED ESCAPE — this is requirement-critical, not defensive noise.
       *
       * `useDismiss` (shell/useDismiss.ts) puts a keydown listener on
       * `document` for EVERY open chrome popover. Without `stopPropagation`,
       * dismissing this panel would also collapse the notification tray or the
       * user menu if either happened to be open, and the user would have no way
       * to close the search box alone.
       *
       * React attaches its handlers to the app root, which is BELOW `document`,
       * so calling `stopPropagation` on the synthetic event stops the native
       * event before those listeners ever see it.
       *
       * Only when the panel is actually open: with it closed, Escape should
       * keep meaning whatever it means to the rest of the shell, and swallowing
       * it here would break the tray for anyone whose focus happens to be in
       * the search box.
       */
      if (!state.open) return;
      event.stopPropagation();
      // `<input type="search">` clears itself on Escape in some browsers. The
      // first Escape closes the panel; it must not also wipe what was typed.
      event.preventDefault();
      state.close();
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      // Nothing to walk. Let the caret keep its normal behaviour.
      if (state.hits.length === 0) return;

      event.preventDefault(); // Stop the caret jumping and the page scrolling.
      setOpen(true);

      const delta = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndexState((current) => {
        const clamped = current >= 0 && current < state.hits.length ? current : -1;
        if (clamped === -1) {
          // Entering the list: Down starts at the top, Up starts at the bottom.
          return delta === 1 ? 0 : state.hits.length - 1;
        }
        // Wrap, so a held arrow key cycles rather than sticking at an end.
        return (clamped + delta + state.hits.length) % state.hits.length;
      });
      return;
    }

    if (event.key === 'Enter') {
      /**
       * ENTER IS A NO-OP UNLESS A ROW IS HIGHLIGHTED.
       *
       * Covers both hazards at once: zero results (nothing to navigate to) and
       * an index that has outlived the list it was taken from — `activeIndex`
       * is already clamped, so `hit` is either a real row or `undefined`, and
       * `undefined` navigates nowhere instead of to `/leads/undefined`.
       */
      if (state.activeIndex < 0) return;
      const hit = state.hits[state.activeIndex];
      if (hit === undefined) return;

      event.preventDefault();
      state.goTo(hit);
    }
  }, []);

  const showMinLengthHint = open && term.trim().length < SEARCH_MIN_QUERY_LENGTH;

  /**
   * "No results" is a claim about a COMPLETED search, so every one of these has
   * to hold: the term was long enough to send, the request has finished, it did
   * not fail, and all four groups came back empty. Getting any of them wrong
   * means telling the user their record does not exist while it is still
   * loading.
   */
  const showNoResults =
    open && isQueryable && !query.isPending && !query.isError && !hasAnyResult(results);

  return {
    term,
    setTerm,
    open,
    close,
    openIfTyped,
    debouncedTerm,
    isQueryable,
    results,
    hits,
    isLoading: isQueryable && query.isPending,
    isError: isQueryable && query.isError,
    activeIndex,
    setActiveIndex: setActiveIndexState,
    showMinLengthHint,
    showNoResults,
    onKeyDown,
    goTo,
  };
}
